import React, { useState, useEffect, useRef } from 'react'
import styles from './ProjectDropdown.module.css'

export default function ProjectDropdown({ 
    userProjects = [], 
    userTasks = [], 
    selectedProject = null,
    selectedTask = null,
    onProjectSelect,
    onTaskSelect,
    onClear,
    trigger
}) {
    const [isOpen, setIsOpen] = useState(false)
    const [filteredTasks, setFilteredTasks] = useState([])
    const [searchQuery, setSearchQuery] = useState('')
    const dropdownRef = useRef(null)

    // Update filtered tasks when selected project changes
    useEffect(() => {
        if (selectedProject) {
            const projectTasks = userTasks
                .filter(task => task.project_id === selectedProject.id)
                .sort((a, b) => a.name.localeCompare(b.name))
            setFilteredTasks(projectTasks)
        } else {
            setFilteredTasks([])
        }
    }, [selectedProject, userTasks])

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false)
                setSearchQuery('')
            }
        }

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside)
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [isOpen])

    const handleProjectClick = (project) => {
        if (selectedProject?.id === project.id) {
            // If same project is clicked, deselect it
            onProjectSelect(null)
            onTaskSelect(null)
        } else {
            // Select new project and clear task
            onProjectSelect(project)
            onTaskSelect(null)
        }
    }

    const handleTaskClick = (task) => {
        if (selectedTask?.id === task.id) {
            // If same task is clicked, deselect it
            onTaskSelect(null)
        } else {
            // Select task
            onTaskSelect(task)
        }
    }

    const handleClearAll = () => {
        onClear()
        setIsOpen(false)
        setSearchQuery('')
    }

    const toggleDropdown = () => {
        setIsOpen(!isOpen)
        setSearchQuery('')
    }

    // Group projects by client and filter based on search query
    const processedData = React.useMemo(() => {
        const clientGroups = {}
        
        userProjects.forEach(project => {
            const clientName = project.client_name || 'No Client'
            
            if (!clientGroups[clientName]) {
                clientGroups[clientName] = {
                    name: clientName,
                    projects: []
                }
            }
            
            clientGroups[clientName].projects.push(project)
        })
        
        // Sort projects within each client alphabetically
        Object.values(clientGroups).forEach(clientData => {
            clientData.projects.sort((a, b) => a.name.localeCompare(b.name))
        })
        
        // Filter based on search query
        if (searchQuery) {
            const filteredGroups = {}
            Object.entries(clientGroups).forEach(([clientName, clientData]) => {
                const matchingProjects = clientData.projects.filter(project =>
                    project.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                    clientName.toLowerCase().includes(searchQuery.toLowerCase())
                )
                
                if (matchingProjects.length > 0) {
                    filteredGroups[clientName] = {
                        ...clientData,
                        projects: matchingProjects.sort((a, b) => a.name.localeCompare(b.name))
                    }
                }
            })
            return filteredGroups
        }
        
        return clientGroups
    }, [userProjects, searchQuery])

    return (
        <div className={styles.projectDropdown} ref={dropdownRef}>
            {/* Trigger button */}
            <div onClick={toggleDropdown}>
                {trigger}
            </div>

            {/* Dropdown content */}
            {isOpen && (
                <div className={styles.dropdownContent}>
                    {/* Search bar */}
                    <div className={styles.searchSection}>
                        <input
                            type="text"
                            className={styles.searchInput}
                            placeholder="Search Project or Client"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            autoFocus
                        />
                    </div>

                    {/* Current selection display */}
                    {(selectedProject || selectedTask) && (
                        <div className={styles.selectionPreview}>
                            <span className={styles.previewLabel}>Current Selection:</span>
                            <span className={styles.previewText}>
                                {selectedProject && selectedTask 
                                    ? `${selectedProject.name} > ${selectedTask.name}`
                                    : selectedProject 
                                        ? selectedProject.name 
                                        : 'No selection'
                                }
                            </span>
                            <button 
                                className={styles.clearButton}
                                onClick={handleClearAll}
                                title="Clear selection"
                            >
                                ×
                            </button>
                        </div>
                    )}

                    {/* Clients list */}
                    <div className={styles.clientsList}>
                        {Object.entries(processedData)
                            .sort(([clientNameA], [clientNameB]) => clientNameA.localeCompare(clientNameB))
                            .map(([clientName, clientData]) => (
                            <div key={clientName} className={styles.clientGroup}>
                                {/* Client header */}
                                <div className={styles.clientHeader}>
                                    <div className={styles.clientInfo}>
                                        <span className={styles.clientName}>{clientData.name}</span>
                                    </div>
                                    <div className={styles.clientStats}>
                                        <span className={styles.projectCount}>
                                            {clientData.projects.length} Projects
                                        </span>
                                    </div>
                                </div>

                                {/* Projects under this client */}
                                <div className={styles.projectsList}>
                                    {clientData.projects.map(project => (
                                        <div key={project.id} className={styles.projectGroup}>
                                            {/* Project item */}
                                            <div 
                                                className={`${styles.projectItem} ${selectedProject?.id === project.id ? styles.selected : ''}`}
                                                onClick={() => handleProjectClick(project)}
                                            >
                                                <div className={styles.projectInfo}>
                                                    <span className={styles.projectDot}>●</span>
                                                    <span className={styles.projectName}>{project.name}</span>
                                                </div>
                                                <div className={styles.projectStats}>
                                                    <span className={styles.taskCount}>
                                                        {userTasks.filter(task => task.project_id === project.id).length} Tasks
                                                    </span>
                                                </div>
                                            </div>

                                            {/* Show tasks when project is selected */}
                                            {selectedProject?.id === project.id && filteredTasks.length > 0 && (
                                                <div className={styles.tasksList}>
                                                    {filteredTasks.map(task => (
                                                        <div 
                                                            key={task.id}
                                                            className={`${styles.taskItem} ${selectedTask?.id === task.id ? styles.selected : ''}`}
                                                            onClick={() => handleTaskClick(task)}
                                                        >
                                                            <span className={styles.taskName}>{task.name}</span>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}

                        {Object.keys(processedData).length === 0 && searchQuery && (
                            <div className={styles.noResults}>
                                No clients or projects found matching "{searchQuery}"
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    )
}